import type { PhotoGalleryCustomerData } from "@contracts/photosPageData";
import type { LocationData } from "@contracts/socialLocations";
import { locationTextMatches } from "./LocationSearch.ts";

// Rows for the "upload by location" page ======================================
// The page shows the customer's upload groups (galleries) and their saved Locations in ONE list:
//  - every gallery is a row, and
//  - every Location/Event that has no gallery yet is an empty row you can drop photos onto.
// The gallery for an empty Location row is only created when the first photo is uploaded to it.
//
// The rules below are checked in this order. Each one prevents a specific problem:
//  1. Only live Locations and Events take part. People are skipped because a photo group can't be assigned to a
//     Person (the backend rejects it), so their row could never accept an upload.
//  2. A gallery only carries a location when that location is in the list from rule 1. A gallery pointing at an
//     archived Location or a Person still shows, as a plain album, so its photos never disappear from the page.
//  3. Row keys. A Location's OLDEST gallery is keyed "loc:{locationId}", the same key its empty row had. Any other
//     gallery is keyed "gal:{galleryId}".
//       - Without this, the key would change from "location" to "gallery" at the moment the first upload creates
//         the gallery. Vue would re-mount the row and the upload still in progress would be thrown away.
//       - It also keeps the row's unsaved-upload queue (kept in localStorage under this key) findable on the next visit.
//       - Oldest, because that is the gallery the backend hands back when asked for a Location's gallery
//         (PhotoGalleryLocationAssigner.FindGalleryForLocation), and it never changes when more galleries are added.
//  4. Order (sortRows): every row has one date and rows sort by it, newest or oldest first as the user picks.
//       - A row with an album uses the moment the album was created.
//       - A Location with no album yet uses the DAY the Location was saved, so a Location added last week shows
//         above an album from last year. It is the day, not the exact time, because the Locations a customer accepts
//         in onboarding are all saved moments apart, in whatever order the import ran. Sorted by exact time, a
//         first-time customer saw their list in an order that read as random. Rounded to the day they tie, and ties
//         sort A to Z.
//     Rows with no usable date go last. See orderRows for why the sort is only applied to rows the page hasn't
//     shown yet.

/** One line on the page: an upload group (gallery), or a Location that has no gallery yet. */
export interface LocationUploadRow {
  /** "loc:{locationId}" or "gal:{galleryId}" (rule 3). */
  key: string;
  /** Null for a Location row until its first upload creates the gallery. */
  gallery: PhotoGalleryCustomerData | null;
  /** Null for an album that isn't assigned to a live Location or Event. */
  location: LocationData | null;
  title: string;
  photoIds: string[];
}

const UNTITLED_ALBUM = "Untitled album";

function normalizeId(id?: string | null): string {
  return id?.trim().toLowerCase() ?? "";
}

function parseTime(value?: string | null): number | null {
  const parsed = Date.parse(value ?? "");
  return Number.isFinite(parsed) ? parsed : null;
}

function createdTime(gallery: PhotoGalleryCustomerData): number {
  return parseTime(gallery.createdOn) ?? 0;
}

function compareOldestFirst(a: PhotoGalleryCustomerData, b: PhotoGalleryCustomerData): number {
  const byDate = createdTime(a) - createdTime(b);
  if (byDate !== 0) return byDate;
  // Same tie-break as the backend so both sides agree on which gallery is a Location's oldest.
  const aId = a.id ?? "";
  const bId = b.id ?? "";
  return aId < bId ? -1 : aId > bId ? 1 : 0;
}

/** Rule 1. Missing/legacy tagKind is treated as a Location, as elsewhere. */
export function isUploadableLocation(location: LocationData | null | undefined): boolean {
  return Boolean(location?.id) && !location.deletedDate && location.tagKind !== "Person";
}

export function buildLocationUploadRows(galleries: PhotoGalleryCustomerData[] | null | undefined, locations: LocationData[] | null | undefined): LocationUploadRow[] {
  const locationsById = new Map<string, LocationData>();
  for (const location of locations ?? []) {
    if (isUploadableLocation(location)) locationsById.set(normalizeId(location.id), location);
  }

  const liveGalleries = (galleries ?? []).filter(gallery => gallery?.id && !gallery.deletedDate);
  const locationOf = (gallery: PhotoGalleryCustomerData): LocationData | null => locationsById.get(normalizeId(gallery.locationId)) ?? null;

  // Rule 3: the first gallery met, oldest first, claims its location's key.
  const primaryGalleryIdByLocationId = new Map<string, string>();
  for (const gallery of [...liveGalleries].sort(compareOldestFirst)) {
    const location = locationOf(gallery);
    if (location && !primaryGalleryIdByLocationId.has(location.id)) primaryGalleryIdByLocationId.set(location.id, gallery.id);
  }

  const galleryRows: LocationUploadRow[] = liveGalleries.map(gallery => {
    const location = locationOf(gallery);
    const isPrimary = Boolean(location) && primaryGalleryIdByLocationId.get(location.id) === gallery.id;
    return {
      key: isPrimary ? `loc:${location.id}` : `gal:${gallery.id}`,
      gallery,
      location,
      title: gallery.groupName?.trim() || location?.name?.trim() || UNTITLED_ALBUM,
      // A photo can be listed twice in older data; a repeated id would also be a repeated Vue key.
      photoIds: [...new Set(gallery.photoIds ?? [])],
    };
  });

  const locationOnlyRows: LocationUploadRow[] = [...locationsById.values()]
    .filter(location => !primaryGalleryIdByLocationId.has(location.id))
    .map(location => ({ key: `loc:${location.id}`, gallery: null, location, title: location.name?.trim() || "Unnamed location", photoIds: [] }));

  return [...galleryRows, ...locationOnlyRows];
}

function compareTitles(a: LocationUploadRow, b: LocationUploadRow): number {
  return a.title.localeCompare(b.title, undefined, { sensitivity: "base" }) || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
}

/** Rule 4: the date a row sorts by, or null when it has none. */
function sortTime(row: LocationUploadRow): number | null {
  if (row.gallery) return parseTime(row.gallery.createdOn);

  const savedAt = parseTime(row.location?.createdDate);
  if (savedAt == null) return null;
  const startOfDay = new Date(savedAt);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay.getTime();
}

/** Rule 4. Returns a new array. */
export function sortRows(rows: LocationUploadRow[], newestFirst: boolean): LocationUploadRow[] {
  const dated: { row: LocationUploadRow; time: number }[] = [];
  const undated: LocationUploadRow[] = [];
  for (const row of rows) {
    const time = sortTime(row);
    if (time == null) undated.push(row);
    else dated.push({ row, time });
  }

  dated.sort((a, b) => (newestFirst ? b.time - a.time : a.time - b.time) || compareTitles(a.row, b.row));
  undated.sort(compareTitles);
  return [...dated.map(entry => entry.row), ...undated];
}

/**
 * Puts rows in the order the page should show them, given the order it showed last time (previousKeys).
 * Rows the page has already shown keep their place; rows it hasn't seen go on top, in the order they came in
 * (the sorted order).
 *
 * A row's place in the sort changes while photos upload: the first photo dropped on a Location creates its album,
 * which makes it the newest one. Re-sorting on every change would move that row out from under the cursor (from
 * wherever it sat to the top of the list). So the sort is only ever applied to rows the page
 * hasn't shown: all of them on the first load or when the user changes the sort (the page then passes no
 * previousKeys), and afterwards just a newly created album, which goes on top where the user can see it.
 */
export function orderRows(rows: LocationUploadRow[], previousKeys: string[]): LocationUploadRow[] {
  const rowsByKey = new Map(rows.map(row => [row.key, row]));
  const known = new Set(previousKeys);
  const newRows = rows.filter(row => !known.has(row.key));
  const keptRows = previousKeys.map(key => rowsByKey.get(key)).filter((row): row is LocationUploadRow => Boolean(row));
  return [...newRows, ...keptRows];
}

export type PhotosFilter = "all" | "withPhotos" | "withoutPhotos";

export interface RowFilters {
  query: string;
  /** A LocationCategory, "Event", or "" for all. */
  category: string;
  photos: PhotosFilter;
}

/**
 * Category uses the same meaning as the Local Promotion report's type filter: "Event" is the tag kind, anything
 * else is a Location's category. An album with no location has no category, so it only shows under "all".
 */
export function rowMatchesFilters(row: LocationUploadRow, filters: RowFilters): boolean {
  const { query, category, photos } = filters;

  if (photos === "withPhotos" && row.photoIds.length === 0) return false;
  if (photos === "withoutPhotos" && row.photoIds.length > 0) return false;

  if (category) {
    const location = row.location;
    if (!location) return false;
    const isEvent = location.tagKind === "Event";
    if (category === "Event" ? !isEvent : isEvent || location.category !== category) return false;
  }

  return locationTextMatches([row.title, row.location?.name, row.location?.city, row.gallery?.comment], query);
}

/** The location's name, when the row's title doesn't already say it (an album named differently from its location). */
export function rowLocationLabel(row: LocationUploadRow): string {
  const name = row.location?.name?.trim() ?? "";
  if (!name) return "";
  return name.localeCompare(row.title.trim(), undefined, { sensitivity: "base" }) === 0 ? "" : name;
}
